import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  BookOpen,
  HelpCircle,
} from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import EntityFooter from "../components/EntityFooter";
import { getForIndustry, getIndustryEnrichment } from "../lib/forIndustries";
import {
  setStandardMeta,
  upsertJsonLd,
  buildArticleSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  derivePaaForIndustry,
} from "../lib/seo";

export default function ForIndustryPage() {
  const { slug } = useParams();
  const data = getForIndustry(slug);
  const enrichment = getIndustryEnrichment(slug);

  useEffect(() => {
    if (!data) return;
    const url = `${window.location.origin}/for/${slug}`;
    setStandardMeta({
      title: data.seoTitle,
      description: data.seoDescription,
      url,
    });

    const paa = enrichment?.paa || derivePaaForIndustry(data);
    upsertJsonLd("for-graph", {
      "@context": "https://schema.org",
      "@graph": [
        buildOrganizationSchema(),
        buildArticleSchema({
          title: data.seoTitle,
          description: data.seoDescription,
          url,
        }),
        buildFaqSchema(paa),
      ],
    });
  }, [data, enrichment, slug]);

  if (!data) {
    return <Navigate to="/" replace />;
  }

  const analyzeHref = `/analyze?industry=${encodeURIComponent(
    data.industryKey
  )}&utm_source=for&utm_campaign=${encodeURIComponent(slug)}`;
  const paa = enrichment?.paa || derivePaaForIndustry(data);

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain" data-testid="for-industry-page">
      <header className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All industries
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 pt-6 pb-20">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          {/* LEFT — Hero pitch */}
          <div className="lg:col-span-3">
            <div
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C26D5C] bg-[#C26D5C]/10 border border-[#C26D5C]/30 rounded-full px-3 py-1.5"
              data-testid="for-industry-pill"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#C26D5C]" />
              {data.sub}
            </div>

            <h1
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F382E] leading-[1.05] tracking-tight mt-5"
              data-testid="for-industry-headline"
            >
              {data.headline}
            </h1>

            <p className="text-base sm:text-lg text-[#1A1A1A] leading-relaxed mt-6 max-w-xl">
              {data.sub_headline}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to={analyzeHref}>
                <Button
                  className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-12 px-7 text-base transition-all duration-300"
                  data-testid="for-industry-cta-analyze"
                >
                  Run a free analysis
                  <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
                </Button>
              </Link>
              <Link to="/learn">
                <Button
                  variant="ghost"
                  className="h-12 px-5 text-[#1F382E] hover:bg-[#E5E0D8]/40 rounded-md text-base"
                >
                  Learn the metrics
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-6 mt-8 text-xs text-[#5C665D]">
              <div className="flex items-center gap-1.5">
                <Sparkles size={13} strokeWidth={1.5} /> Free
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingUp size={13} strokeWidth={1.5} /> Industry benchmarks
              </div>
              <div className="flex items-center gap-1.5">
                <ShieldCheck size={13} strokeWidth={1.5} /> No signup
              </div>
            </div>

            {/* Quotable stat callout — designed to be cited by AI engines */}
            {enrichment?.quickStat && (
              <div
                className="mt-10 flex gap-3 items-start bg-white border-l-2 border-[#C26D5C] border-y border-r border-y-[#E5E0D8] border-r-[#E5E0D8] rounded-r-xl rounded-l-sm p-5"
                data-testid="for-industry-quick-stat"
              >
                <Sparkles
                  size={16}
                  strokeWidth={1.5}
                  className="text-[#C26D5C] mt-0.5 shrink-0"
                />
                <p className="text-sm text-[#1F382E] leading-relaxed">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D] mr-2">
                    Worth quoting
                  </span>
                  {enrichment.quickStat}
                </p>
              </div>
            )}

            {/* Pain points */}
            <section className="mt-12">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                Built for owners and analysts who say…
              </p>
              <ul className="mt-4 space-y-2.5">
                {data.painPoints.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[15px] text-[#1A1A1A] leading-relaxed"
                    data-testid={`for-industry-pain-${i}`}
                  >
                    <span className="font-serif-display text-2xl text-[#C26D5C] leading-none">"</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Deliverables */}
            <section className="mt-12">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                What you'll get
              </p>
              <ul className="mt-4 space-y-3">
                {data.deliverables.map((d, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[15px] text-[#1A1A1A] leading-relaxed"
                  >
                    <Check size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-[#2C5C43]" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* People-also-ask */}
            <section className="mt-14" data-testid="for-industry-paa">
              <div className="flex items-center gap-2">
                <HelpCircle size={16} strokeWidth={1.5} className="text-[#5C665D]" />
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                  People also ask
                </p>
              </div>
              <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight mt-2">
                Common questions about {data.sub.toLowerCase()} financials
              </h2>
              <div className="mt-5 divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
                {paa.map((item, i) => (
                  <details
                    key={i}
                    className="group py-4"
                    data-testid={`for-industry-paa-item-${i}`}
                    {...(i === 0 ? { open: true } : {})}
                  >
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-[15px] font-medium text-[#1F382E] hover:text-[#C26D5C] transition-colors">
                      <span>{item.q}</span>
                      <span className="text-[#5C665D] group-open:rotate-45 transition-transform text-lg leading-none">
                        +
                      </span>
                    </summary>
                    <p className="text-[14.5px] text-[#1A1A1A] leading-relaxed mt-3 pr-6">
                      {item.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          </div>

          {/* RIGHT — Benchmark card */}
          <aside className="lg:col-span-2 lg:sticky lg:top-8">
            <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-7 shadow-sm">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                {data.sub} benchmarks
              </p>
              <ul className="mt-5 divide-y divide-[#E5E0D8]">
                {data.benchmark.map((row) => (
                  <li
                    key={row.k}
                    className="flex items-center justify-between py-3 text-sm gap-3"
                    data-testid={`for-industry-bench-${row.learn}`}
                  >
                    <Link
                      to={`/learn/${row.learn}`}
                      className="text-[#5C665D] hover:text-[#1F382E] underline-offset-2 hover:underline transition-colors flex items-center gap-1.5 min-w-0"
                    >
                      <span className="truncate">{row.k}</span>
                      <BookOpen size={11} strokeWidth={1.5} className="shrink-0 opacity-60" />
                    </Link>
                    <span className="font-medium text-[#1F382E] tabular-nums shrink-0">{row.v}</span>
                  </li>
                ))}
              </ul>
              <Link to={analyzeHref} className="block mt-6">
                <Button
                  variant="outline"
                  className="w-full border-[#1F382E]/20 text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] rounded-md h-11 transition-all duration-300"
                  data-testid="for-industry-cta-compare"
                >
                  Compare your numbers
                  <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
                </Button>
              </Link>
            </div>

            <p className="text-[11px] text-[#5C665D] leading-relaxed mt-4 px-1">
              Benchmarks are mid-range estimates for the sector and may not reflect your specific
              context. Educational insights — not financial advice.
            </p>
          </aside>
        </div>

        <EntityFooter />
      </main>

      <SiteFootnote />
    </div>
  );
}
